import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import * as crawlerApi from 'api/crawlerApi';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import {
  detailActions,
  detailSelectors,
} from 'store/reducers/crawlerReducer/detailSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { useListStore as useApiKeyTemplateListStore } from '../../ApiKeyTemplatePage/hooks';
import {
  CrawlerBaseFormFields,
  mapCrawlerToFormFields,
  mapFormFieldsToCrawlerBaseEditPayload,
} from '../types/crawlerBaseFormFields';

export const useEditBaseStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedCrawler = useAppSelector(detailSelectors.selectedCrawler);
  const isOpenModal = useAppSelector(detailSelectors.isOpenModalEdit);
  const { apiKeyTemplates, fetchApiKeyTemplates } =
    useApiKeyTemplateListStore();

  const form = useForm<CrawlerBaseFormFields>({
    defaultValues: mapCrawlerToFormFields(selectedCrawler || {}),
    mode: 'all',
  });
  const { getValues, reset } = form;

  const isCheckUnnecessaryApiKey = useAppSelector(
    detailSelectors.isCheckUnnecessaryApiKey,
  );

  const setIsCheckUnnecessaryApiKey = useCallback(() => {
    dispatch(detailActions.changeIsCheckUnnecessaryApiKey());
    reset({ apiKeyTemplateId: '' });
  }, [dispatch, reset]);

  useEffect(() => {
    const f = async () => {
      if (selectedCrawler) {
        await fetchApiKeyTemplates();
        reset(mapCrawlerToFormFields(selectedCrawler));
      }
    };
    void f();
  }, [fetchApiKeyTemplates, reset, selectedCrawler]);

  const onCloseModal = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalEdit(false));
  }, [dispatch]);

  const onEditCrawler = useCallback(() => {
    if (selectedCrawler) {
      const requestDelete = async () => {
        dispatch(loaderActions.setLoading(true));
        dispatch(detailActions.updateLoadingStatus(true));
        const values = getValues();
        const result = await crawlerApi.editCrawler(
          selectedCrawler.id,
          mapFormFieldsToCrawlerBaseEditPayload(values),
        );
        if (result.ok) {
          toast({
            status: 'success',
            description: `クローラー基本情報を更新しました。`,
          });

          onCloseModal();
        } else {
          toast({
            status: 'error',
            description: `クローラー基本情報の更新に失敗しました。`,
          });
        }
        dispatch(detailActions.updateLoadingStatus(false));
        dispatch(loaderActions.setLoading(false));
      };

      requestDelete().catch(() => {
        toast({
          status: 'error',
          description: `クローラー基本情報の更新に失敗しました。`,
        });
      });
    }
  }, [selectedCrawler, toast, dispatch, onCloseModal, getValues]);

  const onOpenModalEdit = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalEdit(true));
  }, [dispatch]);

  return {
    ...form,
    /** クローラー基本情報の入力内容 */
    values: getValues(),
    /** クローラー基本情報の更新 */
    onEditCrawler,
    /** クローラー基本情報の更新キャンセル */
    onCloseModal,
    /** クローラー基本情報の更新確認モーダルの表示状態 */
    isOpenModal,
    /** クローラー基本情報の更新確認モーダルを表示 */
    onOpenModalEdit,
    /** APIキーテンプレート */
    apiKeyTemplates,
    /** 選択中APIキーテンプレート */
    selectedApiKeyTemplate:
      apiKeyTemplates.find(
        ({ id }) => id?.toString() === getValues().apiKeyTemplateId,
      ) || null,
    /** APIキーテンプレート不要チェック */
    isCheckUnnecessaryApiKey,
    setIsCheckUnnecessaryApiKey,
  };
};
